import React, { useMemo, useState, useEffect } from 'react';
import { Platform, StyleSheet, View, useWindowDimensions, ActivityIndicator, Linking, TouchableOpacity, Alert } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Text, Button } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useAppColors } from '../../hooks/useAppColors';
import { WEB_MAX_WIDTH } from '../../hooks/useResponsiveLayout';
import { useAuthStore } from '../../stores/authStore';
import { useLegalStatus, useLegalDocuments } from '../../hooks/useLegal';
import { useFamilyMemberships } from '../../hooks/usePayments';
import { useInviteSubUser } from '../../hooks/useFamilies';
import { useLogout } from '../../hooks/useAuth';
import { legalApi } from '../../api/legal';
import { calculateAge, getApiError } from '../../utils/formatters';
import LoadingIndicator from '../../components/common/LoadingIndicator';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';
import LegalDocumentSigner from './LegalDocumentSigner';
import * as Clipboard from 'expo-clipboard';
import QRCode from 'react-native-qrcode-svg';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

const isWeb = Platform.OS === 'web';

export default function LegalGateScreen() {
  const { t, i18n } = useTranslation();
  const colors = useAppColors();
  const { width: screenWidth } = useWindowDimensions();
  const isWide = isWeb && screenWidth > WEB_MAX_WIDTH;
  const insets = useSafeAreaInsets();
  const logout = useLogout();

  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useLegalStatus();
  const { data: docsData, isLoading: docsLoading } = useLegalDocuments(i18n.language);
  const { data: familyMemberships, isLoading: familyMembershipsLoading } = useFamilyMemberships();
  const setNeedsLegalAgreements = useAuthStore((s) => s.setNeedsLegalAgreements);

  const inviteSubUser = useInviteSubUser();
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteExpires, setInviteExpires] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const outstandingItems = useMemo(() => {
    if (!status || !docsData) return [];
    return status.outstanding
      .map((item) => {
        const doc = docsData.documents.find((d) => d.key === item.doc_key);
        if (!doc) return null;
        return { doc, subUserId: item.sub_user_id, subUserName: item.sub_user_name };
      })
      .filter(Boolean) as { doc: NonNullable<typeof docsData.documents[0]>; subUserId: string | null; subUserName: string | null }[];
  }, [status, docsData]);

  const currentItem = outstandingItems[0];
  const currentDoc = currentItem?.doc;
  const total = outstandingItems.length;

  const currentSubUser = useMemo(() => {
    if (!currentItem?.subUserId || !familyMemberships) return null;
    return familyMemberships.find((fm: any) => fm.user_id === currentItem.subUserId);
  }, [currentItem, familyMemberships]);

  const isSubUserAdult = useMemo(() => {
    if (!currentSubUser?.dob) return false;
    return calculateAge(currentSubUser.dob) >= 18;
  }, [currentSubUser]);

  // Automatically generate invite for adult sub-user when displayed
  useEffect(() => {
    if (currentItem?.subUserId && isSubUserAdult && currentDoc?.key === 'guardian_waiver') {
      setInviteToken(null);
      setInviteUrl(null);
      setInviteExpires(null);
      setInviteError(null);
      setInviteCopied(false);
      inviteSubUser.mutate(
        { athlete_id: currentItem.subUserId, regenerate: false },
        {
          onSuccess: (data) => {
            setInviteToken(data.token);
            setInviteUrl(data.url);
            setInviteExpires(data.expires_at);
          },
          onError: (err) => {
            setInviteError(getApiError(err, t('profile.invite.failed'), t));
          },
        }
      );
    }
  }, [currentItem?.subUserId, isSubUserAdult, currentDoc?.key]);

  const copyInviteUrl = async () => {
    if (!inviteUrl) return;
    try {
      await Clipboard.setStringAsync(inviteUrl);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const regenerateInvite = () => {
    if (!currentItem?.subUserId) return;
    setInviteError(null);
    setInviteCopied(false);
    inviteSubUser.mutate(
      { athlete_id: currentItem.subUserId, regenerate: true },
      {
        onSuccess: (data) => {
          setInviteToken(data.token);
          setInviteUrl(data.url);
          setInviteExpires(data.expires_at);
        },
        onError: (err) => {
          setInviteError(getApiError(err, t('profile.invite.failed'), t));
        },
      }
    );
  };

  if (statusLoading || docsLoading || familyMembershipsLoading) {
    return <LoadingIndicator />;
  }

  if (!currentDoc && !showSuccess) {
    // Nothing outstanding — clear gate
    setNeedsLegalAgreements(false);
    return <LoadingIndicator />;
  }

  const handleSuccess = () => {
    if (total <= 1) {
      setShowSuccess(true);
    } else {
      refetchStatus();
    }
  };

  if (showSuccess) {
    return (
      <View style={[styles.screen, { backgroundColor: colors.bg, paddingTop: isWeb ? 0 : insets.top }]}>
        <View style={[styles.content, isWide && styles.contentWide]}>
          <View style={[styles.card, { backgroundColor: isWide ? colors.card : colors.bg, borderColor: colors.border }, isWide && styles.cardElevated]}>
            <View style={styles.successContainer}>
              <View style={[styles.successIconCircle, { backgroundColor: (colors.success ?? colors.accent) + '20' }]}>
                <Icon name="check-circle" size={64} color={colors.success ?? colors.accent} />
              </View>
              <Text style={[styles.successTitle, { color: colors.text }]}>
                {t('legal.allSignedTitle')}
              </Text>
              <Text style={[styles.successMessage, { color: colors.textSecondary }]}>
                {t('legal.allSignedMessage')}
              </Text>
              <Button
                mode="contained"
                onPress={() => setNeedsLegalAgreements(false)}
                style={styles.successButton}
                buttonColor={colors.accent}
                textColor="#fff"
              >
                {t('legal.allSignedButton')}
              </Button>
            </View>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.screen, { backgroundColor: colors.bg, paddingTop: isWeb ? 0 : insets.top }]}>
      <View style={[styles.content, isWide && styles.contentWide]}>
        <View style={[styles.card, { backgroundColor: isWide ? colors.card : colors.bg, borderColor: colors.border }, isWide && styles.cardElevated]}>
          <View style={styles.header}>
            <View style={styles.headerTopRow}>
              <Text style={[styles.title, { color: colors.text }]}>
                {t('legal.gateTitle')}
              </Text>
              <View style={styles.headerActions}>
                <LanguageSwitcher />
                <TouchableOpacity
                  onPress={() => {
                    if (Platform.OS === 'web') { if (window.confirm(t('common.logoutConfirm'))) logout(); }
                    else Alert.alert(t('common.logout'), t('common.logoutConfirm'), [
                      { text: t('common.cancel'), style: 'cancel' },
                      { text: t('common.logout'), style: 'destructive', onPress: logout },
                    ]);
                  }}
                  hitSlop={12}
                >
                  <Icon name="logout" size={22} color={colors.text} />
                </TouchableOpacity>
              </View>
            </View>
            <Text style={[styles.progress, { color: colors.textSecondary }]}>
              {t('legal.progressLabel', { count: total })}
            </Text>
            <Text style={[styles.disclaimer, { color: colors.warning }]}>
              {t('legal.disclaimer')}
            </Text>
          </View>

          {currentDoc?.key === 'guardian_waiver' && isSubUserAdult ? (
            <View style={styles.inviteContainer}>
              <Text style={[styles.inviteTitle, { color: colors.text }]}>
                {t('legal.adultSubUserInviteTitle')}
              </Text>
              <Text style={[styles.inviteBody, { color: colors.textSecondary }]}>
                {t('legal.adultSubUserInviteBody', { name: currentItem.subUserName || '' })}
              </Text>

              {/* Status Section */}
              <View style={[styles.statusCard, { backgroundColor: colors.border + '20', borderColor: colors.border }]}>
                <View style={styles.statusRow}>
                  <Icon 
                    name={currentSubUser?.email ? "email-send-outline" : "account-clock-outline"} 
                    size={20} 
                    color={colors.accent} 
                  />
                  <Text style={[styles.statusText, { color: colors.text }]}>
                    {currentSubUser?.email 
                      ? t('legal.adultSubUserStatusInvited', { email: currentSubUser.email })
                      : t('legal.adultSubUserStatusPending')
                    }
                  </Text>
                </View>
                <Text style={[styles.instructionsText, { color: colors.textSecondary }]}>
                  {currentSubUser?.email
                    ? t('legal.adultSubUserInstructionsInvited')
                    : t('legal.adultSubUserInstructionsPending')
                  }
                </Text>
              </View>

              {inviteSubUser.isPending ? (
                <View style={styles.centerSection}>
                  <ActivityIndicator size="large" color={colors.accent} />
                </View>
              ) : inviteToken && inviteUrl ? (
                <View style={styles.inviteContent}>
                  <View style={styles.qrWrapper}>
                    <QRCode
                      value={inviteUrl}
                      size={180}
                      color={colors.text}
                      backgroundColor={isWide ? colors.card : colors.bg}
                    />
                  </View>

                  <Text style={[styles.linkLabel, { color: colors.textSecondary }]}>
                    {t('profile.invite.linkLabel')}
                  </Text>
                  <View style={[styles.linkBox, { borderColor: colors.border, backgroundColor: colors.bg }]}>
                    <TouchableOpacity
                      onPress={() => Linking.openURL(inviteUrl)}
                      style={styles.linkOpenClick}
                      activeOpacity={0.7}
                    >
                      <Text
                        style={[styles.linkText, { color: colors.accent }]}
                        numberOfLines={1}
                        ellipsizeMode="middle"
                      >
                        {inviteUrl}
                      </Text>
                      <Icon name="open-in-new" size={16} color={colors.accent} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={copyInviteUrl}
                      style={[styles.copyIconBtn, { borderLeftColor: colors.border }]}
                      activeOpacity={0.7}
                    >
                      <Icon
                        name={inviteCopied ? 'check' : 'content-copy'}
                        size={16}
                        color={inviteCopied ? colors.success ?? colors.accent : colors.accent}
                      />
                    </TouchableOpacity>
                  </View>

                  {inviteCopied && (
                    <Text style={[styles.copiedAlert, { color: colors.success ?? colors.accent }]}>
                      {t('profile.invite.copied')}
                    </Text>
                  )}

                  {inviteExpires && (
                    <Text style={[styles.expiryText, { color: colors.textTertiary }]}>
                      {t('profile.invite.expires', { date: new Date(inviteExpires).toLocaleDateString() })}
                    </Text>
                  )}

                  <TouchableOpacity
                    style={[styles.refreshBtn, { borderColor: colors.border }]}
                    onPress={regenerateInvite}
                    disabled={inviteSubUser.isPending}
                    activeOpacity={0.7}
                  >
                    <Icon name="refresh" size={14} color={colors.textSecondary} />
                    <Text style={[styles.refreshBtnText, { color: colors.textSecondary }]}>
                      {t('profile.invite.regenerate')}
                    </Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <View style={styles.centerSection}>
                  <Text style={{ color: colors.error, textAlign: 'center', marginBottom: 12 }}>
                    {inviteError || t('profile.invite.failed')}
                  </Text>
                  <Button mode="contained" onPress={regenerateInvite} buttonColor={colors.accent} textColor="#fff">
                    {t('common.retry')}
                  </Button>
                </View>
              )}

              <View style={styles.footerActions}>
                <Button mode="contained" onPress={handleSuccess} style={styles.footerBtn} buttonColor={colors.accent} textColor="#fff">
                  {t('common.continue')}
                </Button>
              </View>
            </View>
          ) : (
            <LegalDocumentSigner
              key={`${currentDoc.key}-${currentItem.subUserId ?? 'self'}`}
              docKey={currentDoc.key}
              title={
                currentItem.subUserName
                  ? `${t(currentDoc.title)} — ${currentItem.subUserName}`
                  : t(currentDoc.title)
              }
              subUserName={currentItem.subUserName ?? undefined}
              subUserId={currentItem.subUserId ?? undefined}
              subUserAge={currentSubUser?.dob ? calculateAge(currentSubUser.dob) : undefined}
              url={legalApi.getProxyDocUrl(currentDoc.key, i18n.language, currentDoc.version_hash)}
              affirmationKeys={currentDoc.affirmation_keys}
              onSuccess={handleSuccess}
            />
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  content: {
    flex: 1,
    width: '100%',
    alignSelf: 'center',
  },
  headerTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  contentWide: {
    maxWidth: WEB_MAX_WIDTH,
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  card: {
    flex: 1,
  },
  cardElevated: {
    borderRadius: 16,
    borderWidth: 1,
    overflow: 'hidden',
  },
  header: { paddingHorizontal: 16, paddingTop: 16, paddingBottom: 4 },
  title: { fontSize: 22, fontWeight: '700', marginBottom: 6 },
  progress: { fontSize: 13, fontWeight: '600', marginBottom: 4 },
  subtitle: { fontSize: 14, marginBottom: 8 },
  disclaimer: { fontSize: 13, fontWeight: '500', fontStyle: 'italic' },
  statusCard: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 16,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  statusText: {
    fontSize: 14,
    fontWeight: '700',
    flex: 1,
  },
  instructionsText: {
    fontSize: 13,
    lineHeight: 18,
  },
  inviteContainer: {
    flex: 1,
    padding: 16,
  },
  inviteTitle: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
  },
  inviteBody: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: 20,
  },
  centerSection: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  inviteContent: {
    alignItems: 'center',
    marginBottom: 20,
  },
  qrWrapper: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    marginBottom: 16,
    alignSelf: 'center',
  },
  linkLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 6,
    alignSelf: 'flex-start',
    width: '100%',
  },
  linkBox: {
    flexDirection: 'row',
    borderWidth: 1,
    borderRadius: 8,
    height: 44,
    width: '100%',
    overflow: 'hidden',
    alignItems: 'center',
    marginBottom: 8,
  },
  linkOpenClick: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    gap: 8,
  },
  linkText: {
    fontSize: 13,
    fontWeight: '500',
    flex: 1,
  },
  copyIconBtn: {
    width: 44,
    height: 44,
    borderLeftWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  copiedAlert: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  expiryText: {
    fontSize: 11,
    marginTop: 4,
    marginBottom: 12,
  },
  refreshBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderRadius: 16,
    gap: 6,
    marginTop: 8,
  },
  refreshBtnText: {
    fontSize: 12,
    fontWeight: '600',
  },
  footerActions: {
    marginTop: 'auto',
    gap: 12,
    paddingTop: 16,
  },
  footerBtn: {
    borderRadius: 8,
    width: '100%',
  },
  successContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  successIconCircle: {
    width: 120,
    height: 120,
    borderRadius: 60,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  successTitle: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
  },
  successMessage: {
    fontSize: 16,
    lineHeight: 22,
    textAlign: 'center',
    marginBottom: 32,
  },
  successButton: {
    borderRadius: 8,
    paddingHorizontal: 32,
  },
});
